import { useState } from "react";
import { Plus, MoreVertical, Users } from "lucide-react";
import { createUser } from "../../services/userService";
import InviteMemberModal from "../modals/InviteMemberModal";
import { useTeamMembers } from "../../hooks/useTeamMembers";
import { LoadingState, ErrorState, EmptyState } from "../ui/LoadingStates";
import { getAvatarColor, getInitials } from "../../utils/avatarUtils";

export default function TeamManagementTab({
  roles = [],
  showRolesSection = true,
  onEditMember,
  onEditRole
}) {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Use custom hook for fetching team members
  const { teamMembers, loading, error, refresh } = useTeamMembers(50, 0);

  // Handle invite member
  const handleInviteMember = async (userData) => {
    const response = await createUser(userData);

    if (response.success) {
      // Refetch the users list to show the new member
      await refresh();
      return response;
    } else {
      throw new Error(response.message || "Failed to invite member");
    }
  };

  return (
    <div className="space-y-6">
      {/* Team Members Section */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Team Members
            </h3>
            <p className="text-sm text-gray-600">
              Manage your team and their roles
            </p>
          </div>
          <button
            onClick={() => setIsInviteModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Invite Member
          </button>
        </div>

        {/* Team Members List */}
        <div className="space-y-3">
          {loading ? (
            <LoadingState message="Loading team members..." />
          ) : error ? (
            <ErrorState message={error} />
          ) : teamMembers.length > 0 ? (
            // Team members list
            teamMembers.map((member) => {
              const fullName = `${member.first_name} ${member.last_name}`;
              const initials = getInitials(member.first_name, member.last_name);

              return (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-medium ${getAvatarColor(
                        fullName
                      )}`}
                    >
                      {initials}
                    </div>
                    {/* Info */}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {fullName}
                        </span>
                        {member.is_active && (
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        )}
                        {!member.is_active && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{member.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Role & Auth Type */}
                    <div className="text-right">
                      <span className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md">
                        {member.role || "User"}
                      </span>
                    </div>
                    {/* Actions */}
                    <button
                      onClick={() => onEditMember(member)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <EmptyState
              icon={Users}
              message="No team members yet"
              actionLabel="Invite First Team Member"
              onAction={() => setIsInviteModalOpen(true)}
            />
          )}
        </div>
      </div>

      {/* {showRolesSection ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Roles & Permissions
            </h3>
            <p className="text-sm text-gray-600">
              Configure access levels for team members
            </p>
          </div>

          <div className="space-y-3">
            {roles.map((role) => (
              <div
                key={role.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-xl"
              >
                <div>
                  <h4 className="font-medium text-gray-900">{role.name}</h4>
                  <p className="text-sm text-gray-500">{role.description}</p>
                </div>
                <button
                  onClick={() => onEditRole(role)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Edit
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null} */}

      {/* Invite Member Modal */}
      <InviteMemberModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSuccess={handleInviteMember}
      />
    </div>
  );
}
